/**
 * Morph Warp Grep SDK
 * 
 * Fast and accurate code search tool for AI agents.
 * Uses ripgrep locally, or use `remoteCommands` for sandbox environments.
 * 
 * @example Local usage
 * ```typescript
 * // With MorphClient (recommended)
 * import { MorphClient } from '@morphllm/morphsdk';
 * 
 * const morph = new MorphClient({ morphApiKey: process.env.MORPH_API_KEY });
 * const result = await morph.warpGrep.execute({
 *   searchTerm: 'Find authentication middleware',
 *   repoRoot: '.'
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Standalone client
 * import { WarpGrepClient } from '@morphllm/morphsdk/tools/warp-grep';
 * 
 * const client = new WarpGrepClient({ morphApiKey: process.env.MORPH_API_KEY });
 * const result = await client.execute({
 *   searchTerm: 'Find authentication middleware',
 *   repoRoot: '.'
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // As a tool for AI agents
 * import { createWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/anthropic';
 * 
 * const tool = createWarpGrepTool({ repoRoot: '.' });
 * // Pass to Claude, GPT, Gemini, etc.
 * ```
 * 
 * @example Remote sandbox (E2B, Modal, Daytona, etc.)
 * ```typescript
 * import { createMorphWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/anthropic';
 * 
 * const tool = createMorphWarpGrepTool({
 *   repoRoot: '/home/repo',
 *   remoteCommands: {
 *     grep: async (pattern, path) => (await sandbox.run(`rg '${pattern}' '${path}'`)).stdout,
 *     read: async (path, start, end) => (await sandbox.run(`sed -n '${start},${end}p' '${path}'`)).stdout,
 *     listDir: async (path, maxDepth) => (await sandbox.run(`find '${path}' -maxdepth ${maxDepth}`)).stdout,
 *   },
 * });
 * ```
 * 
 * @example Provider-agnostic usage (bring your own tool abstraction)
 * ```typescript
 * import { 
 *   warpGrepInputSchema,
 *   executeToolCall,
 *   WARP_GREP_TOOL_NAME,
 *   WARP_GREP_DESCRIPTION,
 * } from '@morphllm/morphsdk/tools/warp-grep';
 * 
 * // Use with your own tool definition abstraction
 * const toolDef = createToolDef({
 *   name: WARP_GREP_TOOL_NAME,
 *   description: WARP_GREP_DESCRIPTION,
 *   schema: warpGrepInputSchema,
 * });
 * 
 * // Execute
 * const result = await executeToolCall(
 *   { search_term: 'Find auth middleware' },
 *   { repoRoot: '.', morphApiKey: process.env.MORPH_API_KEY }
 * );
 * ```
 */

import { z } from 'zod';

/** Provider-agnostic Zod schema for warp grep input */
export const warpGrepInputSchema = z.object({
  search_term: z.string().describe('Search problem statement that this subagent is supposed to research for'),
});

export type WarpGrepInputSchema = z.infer<typeof warpGrepInputSchema>;

// Core client and functions
export { WarpGrepClient, executeWarpGrep, executeToolCall, formatResult, executeGitHubReadFile, formatGitHubReadFileResult } from './client.js';

// Types
export type {
  WarpGrepClientConfig,
  WarpGrepInput,
  WarpGrepResult,
  WarpGrepContext,
  WarpGrepToolConfig,
  WarpGrepProvider,
  GrepResult,
  ReadResult,
  ListDirectoryEntry,
  RemoteCommands,
  WarpGrepStep,
  GitHubSearchInput,
  GitHubSearchToolConfig,
  GitHubReadFileInput,
  GitHubReadFileResult,
  GitHubReadFileToolConfig,
} from './types.js';

// Prompts
export {
  WARP_GREP_DESCRIPTION,
  WARP_GREP_TOOL_NAME,
  GITHUB_SEARCH_TOOL_NAME,
  GITHUB_SEARCH_DESCRIPTION,
  GITHUB_SEARCH_INPUT_SCHEMA,
  GITHUB_READ_FILE_TOOL_NAME,
  GITHUB_READ_FILE_DESCRIPTION,
  GITHUB_READ_FILE_INPUT_SCHEMA,
} from './prompts.js';

// Providers (re-exported for convenience)
export { LocalRipgrepProvider, RemoteCommandsProvider, createCodeStorageHttpCommands } from './providers/index.js';
export type { CodeStorageHttpConfig } from './providers/index.js';

// Path utilities (for handling model path prediction issues)
export { fixPathRepetition } from './utils/paths.js';

// Agent internals (advanced usage)
export { runWarpGrep, runWarpGrepStreaming } from './agent/index.js';
export type { AgentRunResult, SessionConfig, ChatMessage } from './agent/index.js';

// Internal agent tools (for harness builders)
export { toolGrep, toolRead, toolListDirectory, readFinishFiles, normalizeFinishFiles } from './agent/tools/index.js';

// Provider adapters are available via dedicated import paths:
// - '@morphllm/morphsdk/tools/warp-grep/anthropic'
// - '@morphllm/morphsdk/tools/warp-grep/openai'
// - '@morphllm/morphsdk/tools/warp-grep/gemini'
// - '@morphllm/morphsdk/tools/warp-grep/vercel'
